import React from 'react'
import { Wrapper } from './styles'
import GenericButton from '../../../../components/GenericButton'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { useAppDispatch } from '../../../../store'
import { removeSection, reorderSections, type ISurveySection } from '../../../../store/slices/SurveyTemplateSlice'
import { useTheme } from '@mui/material/styles'
import { notification } from '../../../../components/Notifications'
import { type Question } from '../../types'
import { getSurveySections } from '../../../../store/slices/SurveyTemplateSlice/selectors'

interface SectionActionProps {
  totalSections: number
  section: ISurveySection
}

const SectionAction: React.FC<SectionActionProps> = ({ totalSections, section }) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()

  const hasBranchingFilters = (section: ISurveySection): boolean | undefined => {
    return section?.surveyItems?.some((item: Question) => item?.branchingFilters && item.branchingFilters.length > 0)
  }

  const handleDeleteSection = (): void => {
    if (hasBranchingFilters(section)) {
      notification('This Section contains a Branch section. You cannot delete this section', { variant: 'error' })
      return
    }
    dispatch(removeSection({ section }))
    notification('Section Successfully Deleted', { variant: 'success', subTitle: section.name })
  }

  const iconStyle = {
    width: theme.spacing(12),
    height: theme.spacing(12)
  }

  const surveySections: ISurveySection[] = getSurveySections()
  const sectionIndex = surveySections.findIndex((s: ISurveySection) => s.surveySectionId === section.surveySectionId)

  const getSectionGroupIndices = (sections: ISurveySection[], rootIndex: number): number[] => {
    const indices: number[] = [rootIndex]
    const rootId = sections[rootIndex].surveySectionId
    for (let i = rootIndex + 1; i < sections.length; i++) {
      if (sections[i].parentSurveySectionId && indices.includes(sections.findIndex(s => s.surveySectionId === sections[i].parentSurveySectionId))) {
        indices.push(i)
      }
    }
    return indices
  }

  const moveSectionGroup = (fromIndex: number, toIndex: number): void => {
    const groupIndices = getSectionGroupIndices(surveySections, fromIndex)
    const group = groupIndices.map(idx => surveySections[idx])
    let newSections = surveySections.filter((_, idx) => !groupIndices.includes(idx))
    // Adjust insert index if moving down
    let insertAt = toIndex
    if (toIndex > fromIndex) insertAt = toIndex - group.length + 1
    newSections = [
      ...newSections.slice(0, insertAt),
      ...group,
      ...newSections.slice(insertAt)
    ]
    dispatch(reorderSections(newSections))
    notification('Section and its branches moved', { variant: 'success', subTitle: section.name })
  }

  const handleMoveUp = (): void => {
    if (sectionIndex === 0) {
      notification('Section is already at the top', { variant: 'info' })
      return
    }
    // Find the previous root section
    let prevRoot = -1
    for (let i = sectionIndex - 1; i >= 0; i--) {
      if (surveySections[i].indentation === 0) {
        prevRoot = i
        break
      }
    }
    if (prevRoot === -1) {
      notification('No higher root section found to move above', { variant: 'info' })
      return
    }
    moveSectionGroup(sectionIndex, prevRoot)
  }

  const handleMoveDown = (): void => {
    if (sectionIndex >= surveySections.length - 1) {
      notification('Section is already at the bottom', { variant: 'info' })
      return
    }
    // Find the next root section after this group
    const groupIndices = getSectionGroupIndices(surveySections, sectionIndex)
    let nextRoot = -1
    for (let i = groupIndices[groupIndices.length - 1] + 1; i < surveySections.length; i++) {
      if (surveySections[i].indentation === 0) {
        nextRoot = i
        break
      }
    }
    // Find the end of the next root group (so we insert after all its descendants)
    let insertIndex = surveySections.length - 1
    if (nextRoot !== -1) {
      // Find all indices of the next root group
      const nextGroupIndices = getSectionGroupIndices(surveySections, nextRoot)
      insertIndex = nextGroupIndices[nextGroupIndices.length - 1]
    }
    moveSectionGroup(sectionIndex, insertIndex)
  }

  const isMoveDownDisabled = (
    section: ISurveySection,
    sectionIndex: number,
    surveySections: ISurveySection[],
    totalSections: number
  ): boolean => {
    if (section.indentation === 0) {
      // Find all root sections
      const rootSections = surveySections?.filter(s => s.indentation === 0)
      const lastRootSection = rootSections?.[rootSections.length - 1]
      return section.surveySectionId === lastRootSection?.surveySectionId
    }
    return sectionIndex === totalSections - 1
  }

  return (
    <Wrapper>
      <GenericButton
        onClick={handleDeleteSection}
        icon={<DeleteOutlineOutlinedIcon />}
        buttonType={ButtonType.Secondary}
        disabled={totalSections === 1}
        style={iconStyle}
      />
      {
        section.indentation === 0 &&
        <>
          <GenericButton
            onClick={handleMoveUp}
            icon={<KeyboardArrowUpRoundedIcon />}
            buttonType={ButtonType.Secondary}
            disabled={sectionIndex === 0}
            style={iconStyle}
          />
          <GenericButton
            onClick={handleMoveDown}
            icon={<KeyboardArrowDownRoundedIcon />}
            buttonType={ButtonType.Secondary}
            disabled={isMoveDownDisabled(section, sectionIndex, surveySections, totalSections)}
            style={iconStyle}
          />
        </>
      }
    </Wrapper>
  )
}

export default SectionAction
